import { EventBus } from '@hcie/shared';
import { PlatformService } from '../services/platform-service';

// Viewer is desktop-only: filesystem access is not available in web browsers.
const IS_WEB = !PlatformService.isTauri();

/**
 * @class ViewerCoordinator
 * @description FastStone-style image viewer.
 * Layout: topbar | [folder-panel | large-preview] | filmstrip
 */
export class ViewerCoordinator {
    private isViewerActive: boolean = false;
    private isEditing: boolean = false;
    private exitHintTimeout: any = null;

    private siblingFiles: string[] = [];    // image files in current dir
    private siblingFolders: string[] = [];  // subdirs in current dir
    private currentDirectory: string | null = null;
    private selectedFileIndex: number = -1;
    private largePreviewBlobUrl: string | null = null;
    private isExpanded: boolean = false; // #2097-C: Large centered preview mode
    private _pendingFilePath: string | null = null; // file to select after next scanDirectory

    constructor() {
        this.init();
        (window as any).viewerCoordinatorInstance = this;
        (window as any).navigateViewerUp     = () => this.navigateUp();
        (window as any).changeViewerDirectory = () => this.changeDirectory();
    }

    // ─── Init ────────────────────────────────────────────────

    private init(): void {
        window.addEventListener('keydown', (e: KeyboardEvent) => {
            if (!this.isViewerActive) return;
            const tag = (e.target as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            switch (e.key) {
                case 'Escape':
                    if (this.isExpanded) {
                        this.toggleExpanded(false);
                    } else {
                        this.toggleViewer(false);
                    }
                    break;
                case 'ArrowRight':
                case 'ArrowDown':  e.preventDefault(); this.navigateNext(); break;
                case 'ArrowLeft':
                case 'ArrowUp':    e.preventDefault(); this.navigatePrev(); break;
                case 'Enter':      if (this.isExpanded) this.toggleExpanded(false); break;
                case 'Backspace':  if (this.isExpanded) this.toggleExpanded(false); else this.navigateUp(); break;
                case 'e':
                case 'E':          this.toggleEditMode(); break;
            }
        });

        // Tauri "open-file" drag-drop / OS association
        const tauri = (window as any).__TAURI__;
        if (tauri?.event) {
            tauri.event.listen('open-file', (event: any) => {
                const paths = event.payload as string[];
                if (paths?.length > 0) this.openPath(paths[0]);
            });
        }

        EventBus.on('TOGGLE_VIEWER_MODE', (active?: boolean) => {
            this.toggleViewer(active !== undefined ? active : !this.isViewerActive);
        });

        EventBus.on('TOGGLE_VIEWER_EDIT_MODE', () => {
            if (this.isViewerActive) this.toggleEditMode();
        });

        this.loadPersistedDirectory();
    }

    // ─── Viewer toggle ───────────────────────────────────────

    public toggleViewer(active: boolean): void {
        if (active && IS_WEB) {
            this.showToast('Viewer sadece masaüstü uygulamasında çalışır');
            return;
        }

        this.isViewerActive = active;
        const container = document.getElementById('appContainer');

        if (active) {
            container?.classList.add('viewer-active');
            if (!this.currentDirectory) this.loadPersistedDirectory();
            this.scanDirectory();
            this.showToast('Esc: Kapat  ←→: Gezin  E: Düzenle');
        } else {
            container?.classList.remove('viewer-active', 'viewer-edit-active');
            this.isEditing = false;
            this.persistDirectory();
            this.revokeLargePreviewBlob();
        }

        EventBus.emit('VIEWER_MODE_CHANGED', { active });
    }

    // ─── Edit mode ───────────────────────────────────────────

    public toggleEditMode(): void {
        if (this.selectedFileIndex >= 0) {
            this.loadCurrentIntoEditor();
        }
    }

    public loadCurrentIntoEditor(): void {
        if (this.selectedFileIndex < 0) return;
        const path = this.siblingFiles[this.selectedFileIndex];
        if (!path) return;
        this.showToast(`Yükleniyor: ${this.basename(path)}`);
        EventBus.emit('OPEN_IMAGE_PATH', { path });
        
        // Close viewer on edit (#2097-Refinement)
        this.toggleViewer(false);
    }

    // ─── Directory scan ──────────────────────────────────────

    private async scanDirectory(): Promise<void> {
        if (!this.currentDirectory) {
            this.renderFolderPanel();
            this.renderFilmstrip();
            return;
        }

        try {
            const entries = await PlatformService.readDir(this.currentDirectory);
            const imageExts = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.psd', '.hcie', '.bmp'];

            this.siblingFolders = entries
                .filter(e => e.is_dir)
                .map(e => e.path);

            this.siblingFiles = entries
                .filter(e => !e.is_dir && imageExts.some(ext => e.name.toLowerCase().endsWith(ext)))
                .map(e => e.path);

            const dirLabel = document.getElementById('viewerDirectoryShortName');
            if (dirLabel) dirLabel.textContent = this.basename(this.currentDirectory);

            this.renderFolderPanel();
            this.renderFilmstrip();

            // Resolve pending file-open request (from openPath or OS file association)
            if (this._pendingFilePath) {
                const pendingIdx = this.siblingFiles.indexOf(this._pendingFilePath);
                this._pendingFilePath = null;
                if (pendingIdx >= 0) {
                    this.selectFile(pendingIdx);
                } else if (this.siblingFiles.length > 0) {
                    this.selectFile(0);
                }
            } else if (this.selectedFileIndex < 0 && this.siblingFiles.length > 0) {
                this.selectFile(0);
            } else if (this.selectedFileIndex >= 0) {
                this.updateLargePreview();
                this.updateInfoBar();
                this.updateNavButtons();
            }
        } catch (error) {
            console.error('[Viewer] scanDirectory failed:', error);
            this.showToast(`Klasör okunamadı: ${this.currentDirectory}`);
        }
    }

    // ─── Left panel: folder list ─────────────────────────────

    private renderFolderPanel(): void {
        const panel = document.getElementById('viewerFolderPanel');
        if (!panel) return;
        panel.innerHTML = '';

        if (this.siblingFolders.length === 0) {
            const empty = document.createElement('div');
            empty.style.cssText = 'padding:16px 12px; font-size:11px; color:#555;';
            empty.textContent = 'Alt klasör yok';
            panel.appendChild(empty);
            return;
        }

        this.siblingFolders.forEach(folderPath => {
            const item = document.createElement('div');
            item.className = 'viewer-folder-item';

            const icon = document.createElement('img');
            icon.src = './resources/themes/ie_color/ui/folderclose.svg';
            icon.className = 'viewer-folder-item-icon';
            icon.alt = '';

            const name = document.createElement('span');
            const folderName = this.basename(folderPath);
            name.textContent = folderName;
            name.title = folderName;

            item.appendChild(icon);
            item.appendChild(name);

            item.addEventListener('click', () => {
                this.currentDirectory = folderPath;
                this.selectedFileIndex = -1;
                this.persistDirectory();
                this.scanDirectory();
            });

            panel.appendChild(item);
        });
    }

    // ─── Bottom filmstrip ────────────────────────────────────

    private renderFilmstrip(): void {
        const strip = document.getElementById('viewerFilmstrip');
        if (!strip) return;
        strip.innerHTML = '';

        this.siblingFiles.forEach((filePath, i) => {
            strip.appendChild(this.createStripCard(filePath, i));
        });
    }

    private createStripCard(filePath: string, fileIndex: number): HTMLElement {
        const card = document.createElement('div');
        card.className = 'viewer-strip-card';
        if (fileIndex === this.selectedFileIndex) card.classList.add('selected');
        card.dataset.fileIndex = String(fileIndex);

        const name = this.basename(filePath);
        const isRaster = /\.(png|jpg|jpeg|webp|gif|bmp)$/i.test(name);

        const thumb = document.createElement('div');
        thumb.className = 'viewer-strip-thumb';

        if (isRaster) {
            const img = document.createElement('img');
            img.alt = name;
            img.loading = 'lazy';
            img.src = PlatformService.convertFileSrc(filePath);
            img.addEventListener('error', () => {
                PlatformService.convertFileSrcAsync(filePath).then(blobUrl => {
                    img.src = blobUrl;
                    img.dataset.blobUrl = blobUrl;
                }).catch(() => {
                    img.style.display = 'none';
                    const fb = document.createElement('span');
                    fb.className = 'viewer-thumb-fallback';
                    fb.textContent = name.split('.').pop()?.toUpperCase() || '?';
                    thumb.appendChild(fb);
                });
            }, { once: true });
            thumb.appendChild(img);
        } else {
            const icon = document.createElement('img');
            icon.src = './resources/themes/ie_color/tools/brush.svg';
            icon.style.cssText = 'width:32px;height:32px;opacity:0.35;';
            icon.alt = '';
            thumb.appendChild(icon);
        }

        const label = document.createElement('div');
        label.className = 'viewer-strip-name';
        label.textContent = name;
        label.title = name;

        card.appendChild(thumb);
        card.appendChild(label);

        card.addEventListener('click', () => this.selectFile(fileIndex));
        card.addEventListener('dblclick', () => {
            this.selectFile(fileIndex);
            this.toggleExpanded(true);
        });

        return card;
    }

    // ─── Selection ───────────────────────────────────────────

    private selectFile(fileIndex: number): void {
        if (fileIndex < 0 || fileIndex >= this.siblingFiles.length) return;
        this.selectedFileIndex = fileIndex;

        // Highlight selected strip card and scroll it into view
        const cards = document.querySelectorAll<HTMLElement>('.viewer-strip-card');
        cards.forEach(c => {
            const idx = Number(c.dataset.fileIndex);
            c.classList.toggle('selected', idx === fileIndex);
            if (idx === fileIndex) {
                c.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
        });

        this.updateLargePreview();
        this.updateInfoBar();
        this.updateNavButtons();
    }

    // ─── Center: large preview ───────────────────────────────

    private async updateLargePreview(): Promise<void> {
        const img = document.getElementById('viewerLargePreview') as HTMLImageElement;
        if (!img) return;

        // Add double-click to expand on the preview itself
        if (!img.dataset.bound) {
            img.addEventListener('dblclick', () => this.toggleExpanded());
            img.dataset.bound = 'true';
        }

        if (this.selectedFileIndex < 0) { img.src = ''; return; }

        const path = this.siblingFiles[this.selectedFileIndex];
        if (!path) return;

        this.revokeLargePreviewBlob();
        
        // #2097-D: Aggressive clear to prevent "drawn over" artifact
        img.style.transition = 'none'; // Disable transition for instant clear
        img.style.opacity = '0';
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; 

        const isRaster = /\.(png|jpg|jpeg|webp|gif|bmp)$/i.test(path);
        if (!isRaster) return;

        try {
            const blobUrl = await PlatformService.convertFileSrcAsync(path);
            this.largePreviewBlobUrl = blobUrl;
            img.src = blobUrl;
            img.onload = () => { 
                img.style.transition = ''; // Restore transition
                img.style.opacity = '1'; 
            };
        } catch {
            img.src = PlatformService.convertFileSrc(path);
            img.onload = () => { 
                img.style.transition = ''; // Restore transition
                img.style.opacity = '1'; 
            };
        }
    }

    private updateInfoBar(): void {
        const nameEl = document.getElementById('viewerFilename');
        const metaEl = document.getElementById('viewerFileMeta');
        if (this.selectedFileIndex < 0 || !nameEl || !metaEl) return;

        const path = this.siblingFiles[this.selectedFileIndex];
        const name = this.basename(path);
        const ext = name.split('.').pop()?.toUpperCase() || '';
        const idx = this.selectedFileIndex + 1;
        const total = this.siblingFiles.length;

        nameEl.textContent = name;
        metaEl.textContent = `${ext}  ${idx} / ${total}`;
    }

    private updateNavButtons(): void {
        const btnPrev = document.getElementById('viewerBtnPrev') as HTMLButtonElement;
        const btnNext = document.getElementById('viewerBtnNext') as HTMLButtonElement;
        if (btnPrev) btnPrev.disabled = this.selectedFileIndex <= 0;
        if (btnNext) btnNext.disabled = this.selectedFileIndex >= this.siblingFiles.length - 1;
    }

    // ─── Navigation ──────────────────────────────────────────

    public navigateNext(): void {
        if (this.siblingFiles.length === 0) return;
        const next = Math.min(this.selectedFileIndex + 1, this.siblingFiles.length - 1);
        if (next !== this.selectedFileIndex) this.selectFile(next);
    }

    public navigatePrev(): void {
        if (this.siblingFiles.length === 0) return;
        const prev = Math.max(this.selectedFileIndex - 1, 0);
        if (prev !== this.selectedFileIndex) this.selectFile(prev);
    }

    private async navigateUp(): Promise<void> {
        if (!this.currentDirectory) return;
        const tauri = (window as any).__TAURI__;
        if (!tauri) return;
        try {
            this.currentDirectory = await tauri.path.dirname(this.currentDirectory);
            this.selectedFileIndex = -1;
            this.scanDirectory();
        } catch (e) {
            console.error('[Viewer] navigateUp failed:', e);
        }
    }

    public async changeDirectory(): Promise<void> {
        try {
            const dir = await PlatformService.openFilePicker({ directory: true, title: 'Klasör Seç' }) as string;
            if (dir) {
                this.currentDirectory = dir;
                this.selectedFileIndex = -1;
                this.persistDirectory();
                this.scanDirectory();
                this.showToast(`Klasör: ${this.basename(dir)}`);
            }
        } catch (e) {
            console.error('[Viewer] changeDirectory failed:', e);
        }
    }

    private openPath(filePath: string): void {
        const dir = filePath.replace(/[/\\][^/\\]+$/, '');
        this._pendingFilePath = filePath;

        if (this.currentDirectory !== dir) {
            this.currentDirectory = dir;
            this.selectedFileIndex = -1;
            this.persistDirectory();
        }

        this.toggleViewer(true);
        // scanDirectory will pick up _pendingFilePath and call selectFile once done
        this.scanDirectory();
    }

    // ─── Persistence ─────────────────────────────────────────

    private persistDirectory(): void {
        if (this.currentDirectory) {
            localStorage.setItem('hcie_last_viewer_dir', this.currentDirectory);
        }
    }

    private loadPersistedDirectory(): void {
        const saved = localStorage.getItem('hcie_last_viewer_dir');
        if (saved) this.currentDirectory = saved;
    }

    // ─── Helpers ─────────────────────────────────────────────

    private revokeLargePreviewBlob(): void {
        if (this.largePreviewBlobUrl) {
            URL.revokeObjectURL(this.largePreviewBlobUrl);
            this.largePreviewBlobUrl = null;
        }
    }

    private basename(p: string): string {
        return p.split(/[/\\]/).pop() || p;
    }

    private showToast(text: string, duration: number = 3000): void {
        let hint = document.getElementById('viewerExitHint');
        if (!hint) {
            hint = document.createElement('div');
            hint.id = 'viewerExitHint';
            document.body.appendChild(hint);
        }
        hint.textContent = text;
        hint.classList.add('show');
        if (this.exitHintTimeout) clearTimeout(this.exitHintTimeout);
        this.exitHintTimeout = setTimeout(() => hint?.classList.remove('show'), duration);
    }

    // ─── Expanded Mode ───────────────────────────────────────

    public toggleExpanded(active?: boolean): void {
        this.isExpanded = active !== undefined ? active : !this.isExpanded;
        const shell = document.getElementById('viewerShell');
        if (this.isExpanded) {
            shell?.classList.add('expanded');
            this.showToast('Genişletilmiş Mod (Esc: Geri, ←→: Gez)', 2000);
        } else {
            shell?.classList.remove('expanded');
        }
    }
}

// Singleton
let viewerCoordinator: ViewerCoordinator | null = null;
export function initViewerCoordinator(): ViewerCoordinator {
    if (!viewerCoordinator) {
        viewerCoordinator = new ViewerCoordinator();
    }
    return viewerCoordinator;
}
